import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-purchase-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1500px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Purchase Orders</h1>
            <p class="text-slate-500 text-xs mt-1">Supplier purchasing, approvals, and stock receiving.</p>
          </div>
          <div class="flex items-center gap-2">
            <a routerLink="/admin/financials/purchase-orders/suppliers" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2">
              <i class="ti ti-building-store"></i>Suppliers
            </a>
            <a routerLink="/admin/financials/purchase-orders/templates" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2">
              <i class="ti ti-template"></i>Templates
            </a>
            <a routerLink="/admin/financials/purchase-orders/reports" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2">
              <i class="ti ti-chart-bar"></i>Reports
            </a>
            <a routerLink="/admin/financials/purchase-orders/new" class="h-9 px-3 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] flex items-center gap-2">
              <i class="ti ti-plus text-[16px]"></i>New PO
            </a>
          </div>
        </header>

        <section class="grid grid-cols-1 md:grid-cols-4 gap-3">
          @for (card of summaryCards(); track card.label) {
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3 shadow-sm">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ card.label }}</p>
              <p class="text-xl font-semibold text-slate-950 mt-1">{{ card.value }}</p>
              <p class="text-[11px] text-slate-500 mt-1">{{ card.help }}</p>
            </div>
          }
        </section>

        <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <div class="relative flex-1 min-w-[260px] max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
              <input [(ngModel)]="filters.search" (input)="loadPurchaseOrders()" type="text" placeholder="Search PO, supplier, job or project"
                class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6]">
            </div>
            <select [(ngModel)]="filters.status" (change)="loadPurchaseOrders()" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
              <option value="">All status</option>
              @for (status of statuses; track status) {
                <option [value]="status">{{ status }}</option>
              }
            </select>
          </div>

          <div class="overflow-x-auto min-h-[420px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Purchase Order</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Supplier</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Reference</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Created</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Total</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (po of purchaseOrders(); track po.id) {
                  <tr [routerLink]="['/admin/financials/purchase-orders', po.id]" class="hover:bg-slate-50 cursor-pointer transition-colors">
                    <td class="px-4 py-3">
                      <span class="font-mono text-xs font-semibold px-2 py-1 rounded-md bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6]">{{ po.po_no }}</span>
                      <p class="text-[10px] text-slate-400 mt-1">{{ po.item_count || 0 }} item(s)</p>
                    </td>
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900 text-sm">{{ po.supplier_name }}</p>
                      <p class="text-[10px] text-slate-400">{{ po.supplier_email || '-' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-500">{{ po.model_type === 'General' ? 'General company PO' : po.model_type + ' #' + po.reference_id }}</td>
                    <td class="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      <p>{{ po.order_date | date:'dd MMM yyyy' }}</p>
                      <p class="text-[10px] text-slate-400">{{ po.created_by_name || 'System' }}</p>
                    </td>
                    <td class="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">{{ po.total | currency:'NZD':'symbol':'1.2-2' }}</td>
                    <td class="px-4 py-3 text-center">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(po.status)">{{ po.status }}</span>
                    </td>
                  </tr>
                }
                @if (!isLoading() && purchaseOrders().length === 0) {
                  <tr>
                    <td colspan="6" class="py-16 text-center text-slate-400">
                      <i class="ti ti-clipboard-list text-4xl mb-3 opacity-25"></i>
                      <p class="font-semibold text-sm">No purchase orders found.</p>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  `
})
export class AdminPurchaseOrdersComponent implements OnInit {
  private api = inject(ApiService);
  purchaseOrders = signal<any[]>([]);
  isLoading = signal(false);
  filters: any = { search: '', status: '' };
  statuses = ['Draft', 'Issued', 'Partially Received', 'Received', 'Cancelled'];

  ngOnInit() {
    this.loadPurchaseOrders();
  }

  loadPurchaseOrders() {
    this.isLoading.set(true);
    this.api.getPurchaseOrders(this.filters).subscribe({
      next: (res: any) => {
        this.purchaseOrders.set(res.data?.purchase_orders || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  summaryCards() {
    const orders = this.purchaseOrders();
    const total = orders.reduce((sum, po) => sum + Number(po.total || 0), 0);
    return [
      { label: 'Open POs', value: orders.filter(po => ['Draft', 'Issued', 'Partially Received'].includes(po.status)).length, help: 'Drafts and supplier orders' },
      { label: 'Received', value: orders.filter(po => po.status === 'Received').length, help: 'Stock already updated' },
      { label: 'Suppliers', value: new Set(orders.map(po => po.supplier_id)).size, help: 'In the current view' },
      { label: 'Current Value', value: total.toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' }), help: 'Filtered order total' }
    ];
  }

  statusBadgeClass(status: string): string {
    if (status === 'Received') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Issued' || status === 'Partially Received') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
